import { register } from "@rill/loader";
import { getSessionValue } from "../utils/session-helper";

// Gets cycles by ids
register(
  {
    name: "cycles",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async ({ api }, cycles) => {
    // don't make a request if no ids passed, otherwise it will fetch all cycles unnecessarily and cause delay for accounts with a lot of data
    if (!cycles || !cycles.length) return [];
    const res = await api("cycle", {
      query: { _id: { $in: cycles } },
    });
    return await res.json();
  }
);

register(
  {
    name: "editCycle",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async (
    { api, locals, session },
    {
      cycle,
      feCycles = null,
      athleteFromOrganization = undefined,
      customProjection = undefined,
      flexibleAthlete = undefined,
    }
  ) => {
    const editCycle = getSessionValue(session, "editCycle");
    if (editCycle) return editCycle;

    if (!cycle) return { _id: "" };

    // A Flexible Program is open to every coach in the organization, no matter
    // who created it. It is owned by the CLIENT, so it is fetched as the
    // client (proxyAthlete makes the API's ctx.user the athlete, after its own
    // organization check) instead of scoping by the org owner / creator below.
    if (flexibleAthlete) {
      const res = await api("cycle", {
        query: {
          _id: cycle,
          proxyAthlete: flexibleAthlete,
          ...(customProjection ? { customProjection } : {}),
        },
      });
      return (await res.json())[0];
    }

    const activeUser = locals.activeUser;
    const activeUserToRequestId = locals.activeUserToRequest._id;
    const hasOrganizationId = !!locals.activeUser.organizationId;

    let query = {
      _id: cycle,
      feCycles,
    };

    //if the user is an organization member and also is not the owner
    if (hasOrganizationId && activeUserToRequestId !== activeUser._id) {
      const organizationQuery = athleteFromOrganization
        ? { createdBy: activeUserToRequestId }
        : { owner: activeUserToRequestId };

      query = {
        ...query,
        ...organizationQuery,
      };
    }

    if (customProjection) query.customProjection = customProjection;

    const res = await api("cycle", {
      query,
    });

    return (await res.json())[0];
  }
);

register(
  {
    name: "athleteCycles",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async (
    { api, locals },
    {
      proxyAthlete,
      customLimit,
      customSkip,
      status = "created",
      owner,
      customSort = { createdDate: -1 },
      customProjection,
    } = {}
  ) => {
    return await api("cycle", {
      query: {
        limit: false,
        createdBy: locals.activeUserToRequest._id,
        status: status,
        type: "coach",
        owner: owner || proxyAthlete,
        "programs.0": { $exists: true },
        storeType: { $nin: ["teams", "single-workout", "flexible"] },
        proxyAthlete,
        customLimit,
        customSkip,
        customSort,
        customProjection,
      },
    }).then(async (cycles) => {
      return await cycles.json();
    });
  }
);

register(
  {
    name: "athleteCyclesCount",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async ({ api, locals }, proxyAthlete) => {
    return await api("cycle-count", {
      query: {
        sort: { modifiedDate: -1 },
        createdBy: locals.activeUserToRequest._id,
        owner: proxyAthlete,
        status: "created",
        type: "coach",
        "programs.0": { $exists: true },
        storeType: { $nin: ["teams", "single-workout", "flexible"] },
      },
    }).then(async (cycles) => {
      return await cycles.json();
    });
  }
);

register(
  {
    name: "unPushedAthleteCyclesNames",
    meta: ["cycle"],
  },
  async ({ api, locals }, owner) => {
    return await api("cycle", {
      query: {
        limit: false,
        sort: { createdDate: -1 },
        fields: ["_id", "name"],
        createdBy: locals.activeUserToRequest._id,
        owner,
        type: "coach",
        status: "created",
      },
    }).then(async (cycles) => {
      return await cycles.json();
    });
  }
);

register(
  {
    name: "activeCycles",
    meta: ["cycle"],
  },
  async ({ api }, params) => {
    const customLimit = params ? params.customLimit : null;
    const customSkip = params ? params.customSkip : null;
    const fromMyPrograms = params ? params.fromMyPrograms : null;
    const feCycles = params ? params.feCycles : null;

    const payload = {
      query: {
        customLimit,
        customSkip,
        sort: { createdDate: -1 },
        fromMyPrograms,
        feCycles,
      },
    };
    if (feCycles) {
      payload.query.type = "subscription";
      payload.query.status = "published";
    }
    const cycles = await (await api("cycle", payload)).json();
    return cycles;
  }
);

register(
  {
    name: "activeCyclesCount",
    meta: ["cycle"],
  },
  async ({ api }, params) => {
    const fromMyPrograms = params ? params.fromMyPrograms : null;
    const feCycles = params ? params.feCycles : null;
    const payload = {
      query: {
        fromMyPrograms,
        feCycles,
      },
    };

    if (feCycles) {
      payload.query.type = "subscription";
      payload.query.status = "published";
    }

    const cycles = await api("cycle-count", payload);
    return await cycles.json();
  }
);

// get the cycles for the program library
register(
  {
    name: "libraryCycles",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async ({ api, locals }, params) => {
    const customLimit = params ? params.customLimit : null;
    const customSkip = params ? params.customSkip : null;

    const activeUser = locals.activeUser;
    const activeUserToRequestId = locals.activeUserToRequest._id;
    const hasOrganizationId = !!locals.activeUser.organizationId;

    let query = {
      customLimit,
      customSkip,
    };

    //if the user is an organization member and also is not the owner
    if (hasOrganizationId && activeUserToRequestId !== activeUser._id) {
      query = { ...query, owner: activeUserToRequestId };
    }

    const res = await api("cycle-library", {
      query,
    });
    return await res.json();
  }
);

register(
  {
    name: "libraryCyclesCount",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async ({ api }) => {
    const cycles = await api("cycle-library-count", {
      query: {},
    });
    return await cycles.json();
  }
);

register(
  {
    name: "storePrograms",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    const [published, draft] = await Promise.all([
      api("cycle", {
        query: {
          limit: false,
          sort: { createdDate: -1 },
          status: "published",
          type: "subscription",
          owner: locals.activeUserToRequest._id,
        },
      }).then((cycles) => cycles.json()),
      api("cycle", {
        query: {
          limit: false,
          sort: { createdDate: -1 },
          status: "draft",
          type: "subscription",
          owner: locals.activeUserToRequest._id,
        },
      }).then((cycles) => cycles.json()),
    ]);

    return { published, draft };
  }
);

register(
  {
    name: "previousProgramExercises",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async ({ api }, { cycleId, program }) => {
    const res = await api("cycle-exercises", {
      query: {
        cycle: cycleId,
        program,
        week: 1,
        allWeeks: true,
      },
    });

    return await res.json();
  }
);

register(
  {
    name: "teamPrograms",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    const [published, draft] = await Promise.all([
      api("cycle", {
        query: {
          limit: false,
          sort: { createdDate: -1 },
          status: "published",
          type: "subscription",
          storeType: "teams",
          owner: locals.activeUserToRequest._id,
        },
      }).then((cycles) => cycles.json()),
      api("cycle", {
        query: {
          limit: false,
          sort: { createdDate: -1 },
          status: "draft",
          type: "subscription",
          storeType: "teams",
          owner: locals.activeUserToRequest._id,
        },
      }).then((cycles) => cycles.json()),
    ]);

    return { published, draft };
  }
);

// Saved Workouts for the templates page: coach-type single workouts with no
// publish/draft lifecycle (always "created", assignable directly). Kept separate
// from the store's subscription single-workouts.
register(
  {
    name: "savedWorkouts",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    return await api("cycle", {
      query: {
        limit: false,
        sort: { createdDate: -1 },
        status: "created",
        type: "coach",
        storeType: "single-workout",
        owner: locals.activeUserToRequest._id,
      },
    }).then((cycles) => cycles.json());
  }
);
